import React, { memo } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { isTeamsAppEnabled } from '@jira/platform__teams-app-config/src/index.tsx';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { getUserNavigationItemLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-user-navigation-item-links/index.tsx';
import { Nav4Apps } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-apps/src/index.tsx';
import { Nav4Assets } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-assets/src/ui/index.tsx';
import { CustomerService } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-customer-service/src/index.tsx';
import { Customers } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-customers/src/index.tsx';
import { HelpCenterManagement } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-help-center-management/src/HelpCenterManagement.tsx';
import { Nav4Dashboards } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-dashboards/src/index.tsx';
import { Nav4Filters } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-filters/src/index.tsx';
import { Nav4Operations } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-operations/src/ui/Nav4Operations.tsx';
import { Nav4Plans } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-plans/src/index.tsx';
import { Nav4Projects } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-core/src/index.tsx';
import { Recent } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-recent/src/index.tsx';
import { Nav4Roadmaps } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-roadmaps/src/index.tsx';
import { Nav4RoadmapsNew } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-roadmaps/src/Nav4RoadmapsNew.tsx';
import { Nav4RoadmapsNewWithRecent } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-roadmaps/src/Nav4RoadmapsNewWithRecent.tsx';
import { Starred } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/index.tsx';
import { Teams } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-teams/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { Stub } from '../../constants/stub/main.tsx';
import type { ConfigurableMenuItem$key } from './__generated__/ConfigurableMenuItem.graphql';

export type ConfigurablesProps = {
	isDefaultOpen?: boolean;
	menuData: ConfigurableMenuItem$key;
	menuId: string;
};

export const ConfigurableMenuItem: typeof ConfigurableMenuItemInner =
	memo(ConfigurableMenuItemInner);

function ConfigurableMenuItemInner({
	isDefaultOpen = false,
	menuData,
	menuId,
}: ConfigurablesProps): React.ReactNode {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<ConfigurableMenuItem$key>(
		graphql`
			fragment ConfigurableMenuItem on Query {
				...Nav4Projects
				...Nav4Filters
				...Nav4Plans
				...Nav4Apps
				...Nav4Operations
				...Nav4Roadmaps_query
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		menuData,
	);

	const USER_NAVIGATION_ITEM_LINKS = getUserNavigationItemLinks();

	switch (menuId) {
		case L1_MENU_ID.APPS:
			return <Nav4Apps isExpanded={isDefaultOpen} queryRef={data} />;
		case L1_MENU_ID.ASSETS:
			/* remove this case on
			 * assets_as_an_app_v2 cleanup &&
			 */
			return <Nav4Assets isExpanded={isDefaultOpen} />;
		case L1_MENU_ID.DASHBOARDS:
			return <Nav4Dashboards isExpanded={isDefaultOpen} />;
		case L1_MENU_ID.FILTERS:
			return <Nav4Filters isExpanded={isDefaultOpen} queryRef={data} />;
		// Note: L1_MENU_ID.GOALS is no longer provided to this function, as it is rendered by GAS.
		// See: src/packages/navigation-apps/sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx
		case L1_MENU_ID.GOALS:
			return <Stub menuId={L1_MENU_ID.GOALS} href={USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.GOALS]} />;
		case L1_MENU_ID.OPERATIONS:
			return <Nav4Operations isExpanded={isDefaultOpen} queryRef={data} />;
		case L1_MENU_ID.PLANS:
			return <Nav4Plans isExpanded={isDefaultOpen} queryRef={data} />;
		case L1_MENU_ID.PROJECTS:
			return <Nav4Projects isExpanded={isDefaultOpen} queryRef={data} />;
		case L1_MENU_ID.RECENT:
			return <Recent />;
		case L1_MENU_ID.ROADMAPS: {
			if (fg('jpd-enable-roadmaps-recents-fe')) {
				return <Nav4RoadmapsNewWithRecent isExpanded={isDefaultOpen} queryRef={data} />;
			}

			return fg('jpd-roadmaps-in-jira') ? (
				<Nav4RoadmapsNew isExpanded={isDefaultOpen} />
			) : (
				<Nav4Roadmaps isExpanded={isDefaultOpen} />
			);
		}
		case L1_MENU_ID.STARRED:
			return <Starred />;
		case L1_MENU_ID.TEAMS:
			return !isTeamsAppEnabled() ? <Teams /> : null;
		case L1_MENU_ID.CUSTOMER_SERVICE:
			return <CustomerService />;
		case L1_MENU_ID.CUSTOMERS:
			return <Customers />;
		case L1_MENU_ID.HELP_CENTER_MANAGEMENT:
			// Visibility (license + admin + experiment) is enforced upstream by
			// `useFilterL1MenuItemsByEditionAndLicense` via `useShowHelpCenterManagement`.
			return <HelpCenterManagement />;
		case L1_MENU_ID.ITAM:
			return fg('itam_ftw') ? (
				<Stub menuId={L1_MENU_ID.ITAM} href={USER_NAVIGATION_ITEM_LINKS[L1_MENU_ID.ITAM]} />
			) : null;
		default:
			return null;
	}
}
